import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";

import { DateRangeFilter } from "../../../components/Filters/DateRangeFilter";
import {
  POINT_REDEMPTION_STATUS_LABELS,
  PointRedemptionStatusFilter,
} from "../../../models/pointRedemption";

export interface PointRedemptionFilterValues {
  status: PointRedemptionStatusFilter | null;
  rewardId: number | null;
  dateFrom: Date | null;
  dateTo: Date | null;
}

export const EMPTY_POINT_REDEMPTION_FILTERS: PointRedemptionFilterValues = {
  status: null,
  rewardId: null,
  dateFrom: null,
  dateTo: null,
};

export const hasPointRedemptionFilters = (filters: PointRedemptionFilterValues): boolean =>
  filters.status !== null ||
  filters.rewardId !== null ||
  filters.dateFrom !== null ||
  filters.dateTo !== null;

const STATUS_OPTIONS = (Object.keys(POINT_REDEMPTION_STATUS_LABELS) as PointRedemptionStatusFilter[]).map(
  (value) => ({ label: POINT_REDEMPTION_STATUS_LABELS[value], value })
);

interface PointRedemptionFiltersProps {
  filters: PointRedemptionFilterValues;
  onChange: (filters: PointRedemptionFilterValues) => void;
  rewardOptions: { label: string; value: number }[];
  /** Con "Todos" en el header no hay un catálogo único para elegir el premio. */
  rewardFilterDisabled: boolean;
  onExport: () => void;
  isExporting: boolean;
}

/** Mismo patrón que BenefitFilters y Reglas: desplegables con showClear y un único "Limpiar filtros". */
export const PointRedemptionFilters = ({
  filters,
  onChange,
  rewardOptions,
  rewardFilterDisabled,
  onExport,
  isExporting,
}: PointRedemptionFiltersProps) => {
  const setFilter = <K extends keyof PointRedemptionFilterValues>(
    key: K,
    value: PointRedemptionFilterValues[K]
  ) => onChange({ ...filters, [key]: value });

  // No hay canjes en el futuro: el tope de los dos calendarios es hoy.
  const today = new Date();

  return (
    <div className="grid mt-3 mb-2 row-gap-3">
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            inputId="pointRedemptionStatusFilter"
            className="w-full"
            value={filters.status}
            options={STATUS_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(event: DropdownChangeEvent) => setFilter("status", event.value ?? null)}
            showClear
          />
          <label htmlFor="pointRedemptionStatusFilter">Estado</label>
        </FloatLabel>
      </div>
      <div className="col-12 md:col-3">
        <FloatLabel className="w-full">
          <Dropdown
            inputId="pointRedemptionRewardFilter"
            className="w-full"
            value={filters.rewardId}
            options={rewardOptions}
            optionLabel="label"
            optionValue="value"
            filter
            emptyMessage="No hay premios todavía"
            emptyFilterMessage="No se encontraron premios"
            disabled={rewardFilterDisabled}
            onChange={(event: DropdownChangeEvent) => setFilter("rewardId", event.value ?? null)}
            showClear
          />
          <label htmlFor="pointRedemptionRewardFilter">Premio</label>
        </FloatLabel>
      </div>
      <DateRangeFilter
        variant="filter"
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onDateFromChange={(date) => setFilter("dateFrom", date)}
        onDateToChange={(date) => setFilter("dateTo", date)}
        startDateId="pointRedemptionDateFrom"
        endDateId="pointRedemptionDateTo"
        labelFrom="Canjeado desde"
        labelTo="Canjeado hasta"
        maxDateFrom={filters.dateTo ?? today}
        maxDateTo={today}
        columnClassName="col-12 md:col-3"
      />
      <div className="col-12 flex flex-wrap justify-content-end gap-2">
        <Button
          type="button"
          label="Limpiar filtros"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          size="small"
          disabled={!hasPointRedemptionFilters(filters)}
          onClick={() => onChange(EMPTY_POINT_REDEMPTION_FILTERS)}
        />
        <Button
          type="button"
          label="Exportar CSV"
          icon="pi pi-download"
          outlined
          size="small"
          loading={isExporting}
          onClick={onExport}
        />
      </div>
    </div>
  );
};
